import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import LabelTag from "../tag/LabelTag";
import PhotoTag from "../tag/PhotoTag";

interface PhotoTagDetails {
    tagName: string;
    tagCount: number;
}

interface Props {
    tags: PhotoTagDetails[];
    totalPhotos: number;
    isLoading?: boolean;
}

type TagDisplayMode = "top" | "repeated" | "all";

const PhotoTagsFilter: React.FC<Props> = ({ tags, totalPhotos, isLoading = false }) => {
    const [displayMode, setDisplayMode] = useState<TagDisplayMode>("top");
    const TOP_TAGS_LIMIT = 25;
    
    const repeatedTags = tags.filter(tag => tag.tagCount > 1);
    const displayedTags = displayMode === "all"
        ? tags
        : displayMode === "repeated"
            ? repeatedTags
            : repeatedTags.slice(0, TOP_TAGS_LIMIT);
    const hasMoreRepeatedTags = repeatedTags.length > TOP_TAGS_LIMIT;
    const hasSinglePhotoTags = tags.length > repeatedTags.length;
    const showExpandButton = displayMode === "top"
        ? hasMoreRepeatedTags || hasSinglePhotoTags
        : displayMode === "repeated"
            ? hasSinglePhotoTags || hasMoreRepeatedTags
            : true;
    const expandButtonLabel = displayMode === "top"
        ? hasMoreRepeatedTags
            ? "view more"
            : "view all"
        : displayMode === "repeated"
            ? hasSinglePhotoTags ? "view all" : "view less"
            : "view less";
    const expandTags = () => {
        if (displayMode === "top") {
            setDisplayMode(hasMoreRepeatedTags ? "repeated" : "all");
        } else if (displayMode === "repeated") {
            setDisplayMode(hasSinglePhotoTags ? "all" : "top");
        } else {
            setDisplayMode("top");
        }
    };

    if (isLoading) {
        return (
            <Card className={"mb-2"}>
                <Card.Header style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <div><b>Photo tags</b></div>
                    <small style={{color: "#999"}}>Select a tag to view its photos</small>
                </Card.Header>
                <div className={"card-alt-padding"}>
                    <div className={'loading'}>Loading tags...</div>
                </div>
            </Card>
        );
    }

    return (
        <Card className={"mb-2"}>
            <Card.Header style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <div><b>Photo tags</b></div>
                <small style={{color: "#999"}}>Select a tag to view its photos</small>
            </Card.Header>
            <div className={"card-alt-padding"}>
                <Link to={"/hub/photos"}>
                    <div className={"d-inline-block"}>
                        <LabelTag />
                    </div>
                </Link>
                <div className={"d-inline-block ml-1 mr-1"}>
                    <PhotoTag name={"all"} count={totalPhotos} bold={true} />
                </div>
                {displayedTags.map((tag, index) => (
                    <div key={`${tag.tagName}-${index}`} className={"d-inline-block mr-1"}>
                        <PhotoTag name={tag.tagName} count={tag.tagCount} bold={true} />
                    </div>
                ))}
                {showExpandButton && (
                    <div className={"d-inline-block ml-1"}>
                        <button
                            onClick={expandTags}
                            className="btn btn-sm btn-link"
                            style={{ padding: 0, verticalAlign: "baseline" }}
                        >
                            {expandButtonLabel}
                        </button>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default PhotoTagsFilter;
